import React, { useEffect, useState } from 'react';
import './Dashboard.css';
import { useAuth } from '@/hooks/useAuth';
import { Loading, QRModal, WalletPreview } from '@/components';
import { useCardsStore, useProfileStore, useQRStore, useWalletStore } from '@/stores';
import { createBrandedCardImage, generateCardFileName } from '@/utils/cardImage.utils';
import { downloadVCard } from '@/utils/vcard.utils';
import { API_BASE_URL } from '@/config/constants';
import { 
  User, Mail, Phone, Building2, AlertCircle, QrCode, 
  Briefcase, ArrowRight, Download, Plus,
  CreditCard, Edit, Trash2, Star, RefreshCw, Users, X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Dashboard: React.FC = () => {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  
  // Zustand stores
  const cards = useCardsStore(state => state.cards);
  const defaultCard = useCardsStore(state => state.defaultCard);
  const cardsLoading = useCardsStore(state => state.loading);
  const cardsError = useCardsStore(state => state.error);
  const fetchCards = useCardsStore(state => state.fetchCards);
  const deleteCard = useCardsStore(state => state.deleteCard);
  const setDefaultCardInStore = useCardsStore(state => state.setDefaultCard);
  const maxCardsLimit = useCardsStore(state => state.maxCardsLimit);
  const canCreateCard = useCardsStore(state => state.canCreateCard);
  
  const azureProfile = useProfileStore(state => state.azureProfile);
  const companyInfo = useProfileStore(state => state.companyInfo);
  const profileLoading = useProfileStore(state => state.loading);
  const fetchProfile = useProfileStore(state => state.fetchProfile);
  
  const getCachedOrGenerate = useQRStore(state => state.getCachedOrGenerate);
  // Wallet store for cached contacts
  const walletContacts = useWalletStore(state => state.contacts);
  const walletTotal = useWalletStore(state => state.totalContacts);
  const fetchWalletContacts = useWalletStore(state => state.fetchContacts);

  // Local UI state
  const [defaultQrCode, setDefaultQrCode] = useState<string | null>(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showScanChoice, setShowScanChoice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [walletCount, setWalletCount] = useState<number>(0);
  const [recentContacts, setRecentContacts] = useState<any[]>([]);
  
  const loading = cardsLoading || profileLoading;

  // Load data on mount
  useEffect(() => {
    loadData();
    loadWalletCount();
  }, []);

  // Generate QR when default card changes
  useEffect(() => {
    if (defaultCard) {
      generateQRCode(defaultCard);
    }
  }, [defaultCard?.cardId]);

  const loadData = async () => {
    try {
      setError(null);
      const token = await getAccessToken(true);
      
      // Load both cards and profile (stores handle caching)
      await Promise.all([
        fetchCards(token),
        fetchProfile(token)
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    }
  };

  const loadWalletCount = async () => {
    try {
      const token = await getAccessToken(true);
      await fetchWalletContacts(token);
    } catch (err) {
      console.error('Failed to load wallet count:', err);
    }
  };

  // Sync from wallet store
  useEffect(() => {
    setWalletCount(walletTotal);
    setRecentContacts(walletContacts.slice(0, 3));
  }, [walletContacts, walletTotal]);

  const handleNavigateToWallet = () => {
    navigate('/wallet');
  };

  const handleNavigateToScanCard = () => {
    setShowScanChoice(true);
  };

  const handleScanMyCard = () => {
    setShowScanChoice(false);
    navigate('/cards', { state: { openScanner: true } });
  };

  const handleScanWalletCard = () => {
    setShowScanChoice(false);
    navigate('/wallet', { state: { openScanner: true } });
  };

  const handleManageCards = () => {
    navigate('/cards');
  };

  const handleCreateCard = () => {
    // Check limit before navigation
    if (!canCreateCard()) {
      alert(`You have reached the maximum limit of ${maxCardsLimit} cards. Please delete an existing card to create a new one.`);
      return;
    }
    // No need to pass state - components will use stores
    navigate('/cards/new', { state: { isNewCard: true, fromRoute: '/' } });
  };

  const handleEditCard = (card: any) => {
    navigate(`/cards/edit/${card.cardId}`, { state: { isEdit: true, fromRoute: '/' } });
  };

  const handleViewCard = (card: any) => {
    navigate(`/cards/view/${card.cardId}`, { state: { isViewMode: true, fromRoute: '/' } });
  };

  const handleSetDefaultCard = async (card: any) => {
    try {
      const token = await getAccessToken(true);
      await setDefaultCardInStore(card.cardId, token);
    } catch (err) {
      console.error('Failed to set default card:', err);
      setError('Failed to set default card');
    }
  };

  const handleDeleteCard = async (card: any) => {
    if (!confirm(`Are you sure you want to delete "${card.cardName}"?`)) return;
    
    try {
      const token = await getAccessToken(true);
      await deleteCard(card.cardId, token);
    } catch (err) {
      console.error('Failed to delete card:', err);
      setError('Failed to delete card. Please try again.');
    }
  };

  const generateQRCode = async (card: any) => {
    try {
      const token = await getAccessToken(true);
      const qrData = await getCachedOrGenerate(card.cardId, token, card.qrCodeUrl);
      
      // Use vCard QR only (no tracking)
      setDefaultQrCode(qrData.vCardQR);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
    }
  };

  const handleDownloadQR = async () => {
    if (!defaultQrCode || !defaultCard) return;
    
    try {
      // Extract card data for branded image
      const primaryEmail = defaultCard.emails.find(e => e.is_primary)?.email || defaultCard.emails[0]?.email || '';
      const primaryPhone = defaultCard.phones.find(p => p.is_primary)?.number || defaultCard.phones[0]?.number || '';
      
      const formData = {
        displayName: defaultCard.displayName || '',
        givenName: defaultCard.givenName || '',
        surname: defaultCard.surname || '',
        jobTitle: defaultCard.jobTitle || '',
        companyName: defaultCard.companyName || '',
        department: defaultCard.department || '',
        mail: primaryEmail,
        mobilePhone: primaryPhone,
        businessPhones: [],
        additionalEmails: defaultCard.emails.filter(e => !e.is_primary).map(e => e.email),
        additionalPhones: defaultCard.phones.filter(p => !p.is_primary).map(p => ({ type: p.type, number: p.number })),
        address: defaultCard.addresses[0],
        additionalAddresses: defaultCard.addresses.slice(1),
        website: defaultCard.website || '',
        socialProfiles: defaultCard.socialProfiles || [],
        notes: defaultCard.notes || '',
      };
      
      const brandedImage = await createBrandedCardImage({
        qrCodeUrl: defaultQrCode,
        formData,
        primaryColor: '#1E3A8A',
        secondaryColor: '#FFFFFF',
        azureProfileId: azureProfile?.id,
        getAccessToken: () => getAccessToken(false)
      }, 'png');
      
      const link = document.createElement('a');
      link.href = brandedImage;
      link.download = generateCardFileName(defaultCard.displayName, 'png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download QR:', error);
    }
  };

  const handleDownloadVCard = () => {
    if (!defaultCard) return;
    
    const primaryEmail = defaultCard.emails.find(e => e.is_primary)?.email || defaultCard.emails[0]?.email || '';
    const primaryPhone = defaultCard.phones.find(p => p.is_primary)?.number || defaultCard.phones[0]?.number || '';
    
    // Create BusinessCardData format for vCard utility
    const vCardData: any = {
      displayName: defaultCard.displayName || '',
      givenName: defaultCard.givenName || '',
      surname: defaultCard.surname || '',
      jobTitle: defaultCard.jobTitle || '',
      companyName: defaultCard.companyName || '',
      department: defaultCard.department || '',
      mail: primaryEmail,
      mobilePhone: primaryPhone,
      businessPhones: [],
      additionalEmails: defaultCard.emails.filter(e => !e.is_primary).map(e => e.email),
      additionalPhones: defaultCard.phones.filter(p => !p.is_primary).map(p => ({ type: p.type, number: p.number })),
      address: defaultCard.addresses[0],
      additionalAddresses: defaultCard.addresses.slice(1),
      website: defaultCard.website || '',
      socialProfiles: defaultCard.socialProfiles || [],
      notes: defaultCard.notes || '',
    };
    
    downloadVCard(vCardData);
  };

  const handleShare = async () => {
    if (!defaultQrCode || !defaultCard) return;
    
    if ('share' in navigator) {
      try {
        // Extract card data for branded image
        const primaryEmail = defaultCard.emails.find(e => e.is_primary)?.email || defaultCard.emails[0]?.email || '';
        const primaryPhone = defaultCard.phones.find(p => p.is_primary)?.number || defaultCard.phones[0]?.number || '';
        
        const formData = {
          displayName: defaultCard.displayName || '',
          givenName: defaultCard.givenName || '',
          surname: defaultCard.surname || '',
          jobTitle: defaultCard.jobTitle || '',
          companyName: defaultCard.companyName || '',
          department: defaultCard.department || '',
          mail: primaryEmail,
          mobilePhone: primaryPhone,
          businessPhones: [],
          additionalEmails: defaultCard.emails.filter(e => !e.is_primary).map(e => e.email),
          additionalPhones: defaultCard.phones.filter(p => !p.is_primary).map(p => ({ type: p.type, number: p.number })),
          address: defaultCard.addresses[0],
          additionalAddresses: defaultCard.addresses.slice(1),
          website: defaultCard.website || '',
          socialProfiles: defaultCard.socialProfiles || [],
          notes: defaultCard.notes || '',
        };
        
        const brandedImage = await createBrandedCardImage({
          qrCodeUrl: defaultQrCode,
          formData,
          primaryColor: '#1E3A8A',
          secondaryColor: '#FFFFFF',
          azureProfileId: azureProfile?.id,
          getAccessToken: () => getAccessToken(false)
        }, 'png');
        
        const response = await fetch(brandedImage);
        const blob = await response.blob();
        const file = new File([blob], generateCardFileName(defaultCard.displayName, 'png'), { type: 'image/png' });
        
        await navigator.share({
          title: `${defaultCard.cardName} - Business Card`,
          text: `Check out ${defaultCard.cardName}'s digital business card`,
          files: [file]
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    }
  };

  const handleEmailQR = async () => {
    if (!defaultCard || !defaultQrCode) return;
    
    try {
      // Extract card data for branded image
      const primaryEmail = defaultCard.emails.find(e => e.is_primary)?.email || defaultCard.emails[0]?.email || '';
      const primaryPhone = defaultCard.phones.find(p => p.is_primary)?.number || defaultCard.phones[0]?.number || '';
      
      const formData = {
        displayName: defaultCard.displayName || '',
        givenName: defaultCard.givenName || '',
        surname: defaultCard.surname || '',
        jobTitle: defaultCard.jobTitle || '',
        companyName: defaultCard.companyName || '',
        department: defaultCard.department || '',
        mail: primaryEmail,
        mobilePhone: primaryPhone,
        businessPhones: [],
        additionalEmails: defaultCard.emails.filter(e => !e.is_primary).map(e => e.email),
        additionalPhones: defaultCard.phones.filter(p => !p.is_primary).map(p => ({ type: p.type, number: p.number })),
        address: defaultCard.addresses[0],
        additionalAddresses: defaultCard.addresses.slice(1),
        website: defaultCard.website || '',
        socialProfiles: defaultCard.socialProfiles || [],
        notes: defaultCard.notes || '',
      };
      
      const brandedImage = await createBrandedCardImage({
        qrCodeUrl: defaultQrCode,
        formData,
        primaryColor: '#1E3A8A',
        secondaryColor: '#FFFFFF',
        azureProfileId: azureProfile?.id,
        getAccessToken: () => getAccessToken(false)
      }, 'png');
      
      const fileName = generateCardFileName(defaultCard.displayName, 'png');
      const blob = await fetch(brandedImage).then(r => r.blob());
      const file = new File([blob], fileName, { type: 'image/png' });

      // Use Web Share API with file attachment
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `${defaultCard.cardName} - Digital Business Card`,
          text: `${defaultCard.displayName}${defaultCard.jobTitle ? ' | ' + defaultCard.jobTitle : ''}`,
          files: [file],
        });
      } else {
        // Fallback: download + mailto
        const link = document.createElement('a');
        link.href = brandedImage;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => {
          const subject = encodeURIComponent(`${defaultCard.cardName} - Digital Business Card`);
          const body = encodeURIComponent(`Hi,\n\nPlease find my digital business card attached (just downloaded to your device).\n\nBest regards,\n${defaultCard.displayName}\n${defaultCard.jobTitle || ''}\n${defaultCard.companyName || ''}`);
          window.location.href = `mailto:?subject=${subject}&body=${body}`;
        }, 500);
      }
    } catch (error) {
      console.error('Error preparing email:', error);
    }
  };

  if (loading) {
    return <Loading message="Loading your profile..." />;
  }

  if (error || cardsError) {
    return (
      <div className="dashboard-container">
        <div className="error-card">
          <AlertCircle size={48} color="#DC2626" />
          <h2>Error Loading Profile</h2>
          <p>{error || cardsError}</p>
          <button onClick={loadData} className="btn btn-primary">
            <RefreshCw size={18} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const primaryEmail = azureProfile?.mail || '';
  const primaryPhone = azureProfile?.mobilePhone || azureProfile?.businessPhones?.[0] || '';

  return (
    <div className="dashboard-container">
      {/* Top Section: Profile and Quick Actions */}
      <div className="dashboard-top-section">
        {/* User Profile Card */}
        <div className="user-profile-card">
          <div className="profile-header">
            <h3 className="profile-section-title">Your Profile</h3>
          </div>
          <div className="profile-content">
            <div className="profile-avatar">
              {azureProfile?.photo ? (
                <img src={azureProfile.photo} alt={azureProfile.displayName} />
              ) : (
                <div className="avatar-placeholder">
                  <User size={46} />
                </div>
              )}
            </div>
            <div className="profile-details">
              <h2 className="profile-name">{azureProfile?.displayName}</h2>
              {azureProfile?.jobTitle && (
                <div className="profile-info-item">
                  <Briefcase size={16} />
                  <span>{azureProfile.jobTitle}</span>
                </div>
              )}
              {companyInfo && (
                <div className="profile-info-item">
                  <Building2 size={16} />
                  <span>{companyInfo.companyName}</span>
                </div>
              )}
              {primaryEmail && (
                <div className="profile-info-item">
                  <Mail size={16} />
                  <span>{primaryEmail}</span>
                </div>
              )}
              {primaryPhone && (
                <div className="profile-info-item">
                  <Phone size={16} />
                  <span>{primaryPhone}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Quick Actions + Wallet Preview */}
        <div className="quick-actions-column">
          {/* Quick Action Buttons */}
          <div className="quick-actions-buttons">
            <button 
              onClick={handleNavigateToScanCard}
              className="quick-action-btn scan-btn"
            >
              <QrCode size={22} />
              <div className="btn-content">
                <span className="btn-title">Scan Business Card</span>
                <span className="btn-subtitle">QR or Physical Card</span>
              </div>
            </button>

            <button 
              onClick={handleCreateCard}
              className="quick-action-btn create-btn"
              disabled={!canCreateCard()}
            >
              <CreditCard size={22} />
              <div className="btn-content">
                <span className="btn-title">Create New Card</span>
                <span className="btn-subtitle">{cards.length}/{maxCardsLimit} cards used</span>
              </div>
            </button>

            <button 
              onClick={handleNavigateToWallet}
              className="quick-action-btn wallet-btn"
            >
              <Users size={22} />
              <div className="btn-content">
                <span className="btn-title">Contact Wallet</span>
                <span className="btn-subtitle">{walletCount} contacts</span>
              </div>
            </button>
          </div>

          {/* Wallet Preview - Recent Contacts */}
          <WalletPreview contacts={recentContacts} totalCount={walletCount} />
        </div>
      </div>

      {/* Main Content: Default Card & All Cards */}
      <div className="dashboard-main-content">
        {/* Default Business Card Section */}
        {defaultCard ? (
          <div className="default-card-section">
          <div className="section-header">
            <div className="section-title">
              <Star size={20} fill="currentColor" className="star-icon" />
              <h3>Default Business Card</h3>
            </div>
            <button onClick={() => handleEditCard(defaultCard)} className="btn-edit">
              <Edit size={16} />
              <span>Edit Card</span>
            </button>
          </div>

          <div className="default-card-content">
            <div className="card-details">
              <div className="card-name-header">
                <QrCode size={18} />
                <span className="card-name-badge">{defaultCard.cardName}</span>
              </div>
              
              <h2 className="card-display-name">{defaultCard.displayName}</h2>
              
              {defaultCard.jobTitle && (
                <div className="card-info-row">
                  <Briefcase size={16} />
                  <span>{defaultCard.jobTitle}</span>
                </div>
              )}
              
              {defaultCard.companyName && (
                <div className="card-info-row">
                  <Building2 size={16} />
                  <span>{defaultCard.companyName}</span>
                </div>
              )}

              {defaultCard.emails && defaultCard.emails.length > 0 && (
                <div className="card-info-row">
                  <Mail size={16} />
                  <span>{defaultCard.emails.find(e => e.is_primary)?.email || defaultCard.emails[0].email}</span>
                </div>
              )}

              {defaultCard.phones && defaultCard.phones.length > 0 && (
                <div className="card-info-row">
                  <Phone size={16} />
                  <span>{defaultCard.phones.find(p => p.is_primary)?.number || defaultCard.phones[0].number}</span>
                </div>
              )}
            </div>

            <div className="card-qr-section">
              <div className="qr-wrapper">
                {defaultQrCode ? (
                  <img 
                    src={defaultQrCode} 
                    alt="QR Code" 
                    className="qr-code-image" 
                    onClick={() => setShowQrModal(true)}
                  />
                ) : (
                  <div className="qr-loading">
                    <QrCode size={48} />
                    <span>Generating...</span>
                  </div>
                )}
              </div>
              <button onClick={handleDownloadQR} className="btn-download-qr">
                <Download size={18} />
                Download QR Code
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="no-default-card">
          <QrCode size={56} />
          <h3>No Default Card Set</h3>
          <p>Create a business card and set it as default to see it here</p>
          <button 
            onClick={handleCreateCard}
            className="btn-primary-action"
            disabled={!canCreateCard()}
          >
            <Plus size={18} />
            Create Your First Card
          </button>
        </div>
      )}

        {/* All Business Cards Section */}
        <div className="all-cards-section">
          <div className="section-header">
            <div className="section-title">
              <QrCode size={20} />
              <h3>All Business Cards</h3>
              <span className="count-badge">{cards.length}</span>
            </div>
            {cards.length > 0 && (
              <button onClick={handleManageCards} className="btn-manage">
                Manage All
                <ArrowRight size={16} />
              </button>
            )}
          </div>

          {cards.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <QrCode size={56} />
              </div>
              <h3>No business cards yet</h3>
              <p>Create your first digital business card to start networking</p>
            </div>
          ) : (
            <div className="cards-grid">
              {cards.map((card) => (
                <div
                  key={card.cardId}
                  className={`card-item ${card.isDefault ? 'is-default' : ''}`}
                  onClick={() => handleViewCard(card)}
                >
                  {card.isDefault && (
                    <div className="default-badge-corner">
                      <Star size={14} fill="currentColor" />
                      Default
                    </div>
                  )}

                  <div className="card-item-header">
                    <QrCode size={18} />
                    <span className="card-name">{card.cardName}</span>
                  </div>

                  <div className="card-item-body">
                    <h4>{card.displayName}</h4>
                    {card.jobTitle && <p className="card-job">{card.jobTitle}</p>}
                    {card.companyName && <p className="card-company">{card.companyName}</p>}
                  </div>

                  <div className="card-item-meta">
                    {card.emails.length > 0 && (
                      <span><Mail size={12} /> {card.emails.length}</span>
                    )}
                    {card.phones.length > 0 && (
                      <span><Phone size={12} /> {card.phones.length}</span>
                    )}
                  </div>

                  <div className="card-item-actions" onClick={(e) => e.stopPropagation()}>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditCard(card);
                      }}
                      className="btn-icon"
                      title="Edit"
                    >
                      <Edit size={16} />
                    </button>
                    {!card.isDefault && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSetDefaultCard(card);
                        }}
                        className="btn-icon"
                        title="Set as default"
                      >
                        <Star size={16} />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCard(card);
                      }}
                      className="btn-icon danger"
                      title={card.isDefault ? "Cannot delete default card" : "Delete"}
                      disabled={card.isDefault || cards.length === 1}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* QR Code Modal */}
      <QRModal
        isOpen={showQrModal && !!defaultQrCode}
        qrCodeUrl={defaultQrCode || ''}
        title="Scan QR Code"
        description="Scan with your phone to save contact"
        onClose={() => setShowQrModal(false)}
        onDownload={handleDownloadQR}
        onDownloadVCard={handleDownloadVCard}
        onShare={handleShare}
        onEmail={handleEmailQR}
      />

      {/* Scan Choice Dialog */}
      {showScanChoice && (
        <div className="scan-choice-overlay" onClick={() => setShowScanChoice(false)}>
          <div className="scan-choice-modal" onClick={(e) => e.stopPropagation()}>
            <button className="scan-choice-close" onClick={() => setShowScanChoice(false)}>
              <X size={20} />
            </button>
            <h3 className="scan-choice-title">What would you like to scan?</h3>
            <div className="scan-choice-options">
              <button className="scan-choice-option" onClick={handleScanWalletCard}>
                <Users size={28} />
                <div className="scan-choice-text">
                  <span className="scan-choice-label">Scan a Contact's Card</span>
                  <span className="scan-choice-desc">Save their contact info to your wallet</span>
                </div>
              </button>
              <button className="scan-choice-option" onClick={handleScanMyCard}>
                <CreditCard size={28} />
                <div className="scan-choice-text">
                  <span className="scan-choice-label">Scan My Business Card</span>
                  <span className="scan-choice-desc">Create a digital business card from your physical card</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
